import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  InternalApiAttributeType,
  InternalApiExportFileType,
  InternalApiLogicalOperator,
  InternalApiSearchFilter,
  InternalApiSearchOperator,
  InternalApiSortDirection,
  InternalApiTransaction,
  InternalApiTransactionSearchQuery,
} from '../api-internal';
import TransactionSearchForm from './TransactionSearchForm';
import SalvPage from '../common/SalvPage';
import { SearchParamKeys } from '../common/searchParamKeys';
import { useQueryTransactions } from './useQueryTransactions';
import TransactionSearchResults from './TransactionSearchResults';
import DataLoadingErrorFallback from '../common/errors/DataLoadingErrorFallback';
import { SalvButton, SalvProgressCircle, Size } from '@salvapp/ui-components';
import { transactionApi } from '../apis';
import useExportDownloader from '../common/useExportDownloader';
import ExportModal from './ExportModal';
import { getCasePath } from '../navigation/helper';
import {
  useAttachCaseTransactions,
  useQueryCaseTransactionsPage,
} from '../case/attachments/transactions/caseTransactionQueryActions';
import { useDispatch } from 'react-redux';
import { showFeedback } from '../feedback/feedbackReducer';
import { CASE_TRANSACTIONS_SCROLL_NAME, LIMIT } from '../case/attachments/transactions/CaseTransactions';
import domIds from '../common/domIds';
import { Row } from '@tanstack/react-table';

export type TransactionSearchQuery = Omit<InternalApiTransactionSearchQuery, 'size'>;

export enum GeneralField {
  EXTERNAL_ID = 'externalId',
  PERSON_EXTERNAL_ID = 'personExternalId',
}

export const defaultSearchFilter: InternalApiSearchFilter = {
  query: undefined,
  attributeIdentifier: GeneralField.EXTERNAL_ID,
  attributeType: InternalApiAttributeType.GENERAL,
  operator: InternalApiSearchOperator.EXISTS,
  not: false,
};

export const newSubQueryFilter = {
  ...defaultSearchFilter,
  operator: InternalApiSearchOperator.IS_EXACTLY,
};

export const defaultSearchQuery: TransactionSearchQuery = {
  filters: [defaultSearchFilter],
  logicalOperator: InternalApiLogicalOperator.AND,
  page: 0,
  orderBy: undefined,
  direction: undefined,
};

enum ViewType {
  DEFAULT,
  SELECTING_CASE_ATTACHMENTS,
}

export const TransactionSearch = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedRows, setSelectedRows] = useState<InternalApiTransaction[]>([]);

  const caseHandle = searchParams.get(SearchParamKeys.caseHandle) ?? '';
  const viewType = caseHandle ? ViewType.SELECTING_CASE_ATTACHMENTS : ViewType.DEFAULT;

  useEffect(() => {
    if (viewType === ViewType.SELECTING_CASE_ATTACHMENTS) {
      document.getElementById(domIds.getMainContentId())?.scrollTo(0, 0);
    }
  }, [viewType]);

  useExportDownloader();

  const initialFilterAmount = searchParams.getAll(SearchParamKeys.operator).length;
  const initialValues: TransactionSearchQuery = {
    logicalOperator:
      (searchParams.get(SearchParamKeys.logicalOperator) as InternalApiLogicalOperator) ||
      defaultSearchQuery.logicalOperator,
    filters: initialFilterAmount
      ? Array.from({ length: initialFilterAmount }, (_, index) => {
          const attributeIdentifier = searchParams.getAll(SearchParamKeys.attributeIdentifier)[index];
          const attributeType = searchParams.getAll(SearchParamKeys.attributeType)[index] as InternalApiAttributeType;
          const query = searchParams.getAll(SearchParamKeys.query)[index] || undefined;
          const operator = searchParams.getAll(SearchParamKeys.operator)[index] as InternalApiSearchOperator;
          const not = searchParams.getAll(SearchParamKeys.not)[index] === 'true';

          return {
            attributeIdentifier,
            attributeType,
            query,
            operator,
            not,
          };
        })
      : defaultSearchQuery.filters,
    orderBy: searchParams.get(SearchParamKeys.orderBy) || defaultSearchQuery.orderBy,
    direction:
      (searchParams.get(SearchParamKeys.direction) as InternalApiSortDirection) || defaultSearchQuery.direction,
    page: searchParams.get(SearchParamKeys.page)
      ? Number(searchParams.get(SearchParamKeys.page))
      : defaultSearchQuery.page,
  };

  const [submittedSearch, setSubmittedSearch] = useState<TransactionSearchQuery>(initialValues);

  const {
    data: transactionsPage,
    isError: isTransactionSearchError,
    isPending: isTransactionSearchPending,
    isFetching: isTransactionSearchFetching,
    error: transactionError,
  } = useQueryTransactions(submittedSearch);

  const {
    data: caseTransactionsPage,
    isError: isCaseTransactionsPageError,
    error: caseTransactionsPageError,
  } = useQueryCaseTransactionsPage({
    handle: caseHandle,
    page: 0,
    size: LIMIT,
  });

  const canAttachTransactionToCase = useMemo(() => {
    const caseTransactionIds = caseTransactionsPage
      ? new Set(caseTransactionsPage.content.map((it) => it.transaction.id))
      : new Set();

    return (row: Row<InternalApiTransaction>) => !caseTransactionIds.has(row.original.id);
  }, [caseTransactionsPage]);

  if (isTransactionSearchError || isCaseTransactionsPageError) {
    return (
      <DataLoadingErrorFallback
        message={"Could not load Transactions' resources"}
        error={[transactionError, caseTransactionsPageError]}
      />
    );
  }

  const exportTransactions = async (fileType: InternalApiExportFileType) => {
    await transactionApi.exportTransactions({
      internalApiTransactionExportQuery: {
        orderBy: submittedSearch.orderBy,
        direction: submittedSearch.direction,
        filters: submittedSearch.filters,
        logicalOperator: submittedSearch.logicalOperator,
        fileType,
      },
    });
  };

  const pageActions =
    viewType === ViewType.SELECTING_CASE_ATTACHMENTS ? (
      <CaseActions handle={caseHandle} selectedRows={selectedRows} />
    ) : (
      <SalvButton label={'Export'} onClick={() => setShowExportModal(true)} size={Size.SMALL} />
    );

  return (
    <SalvPage
      title={viewType === ViewType.SELECTING_CASE_ATTACHMENTS ? 'Select Transactions to Attach' : 'Transactions'}
      breadcrumb={
        viewType === ViewType.SELECTING_CASE_ATTACHMENTS ? { title: 'Case', to: getCasePath(caseHandle) } : undefined
      }
      actions={pageActions}
    >
      <TransactionSearchForm
        isTransactionSearchFetching={isTransactionSearchFetching}
        initialValues={initialValues}
        setSubmittedSearch={setSubmittedSearch}
        setSearchParams={setSearchParams}
      />
      {isTransactionSearchPending ? (
        <SalvProgressCircle ariaLabel={"Loading Transactions' resources"} />
      ) : (
        <TransactionSearchResults
          transactionsPage={transactionsPage}
          isTransactionSearchFetching={isTransactionSearchFetching}
          submittedSearchState={submittedSearch}
          setSubmittedSearch={setSubmittedSearch}
          setSearchParams={setSearchParams}
          selectedRows={viewType === ViewType.SELECTING_CASE_ATTACHMENTS ? selectedRows : undefined}
          setSelectedRows={viewType === ViewType.SELECTING_CASE_ATTACHMENTS ? setSelectedRows : undefined}
          isRowSelectionEnabled={
            viewType === ViewType.SELECTING_CASE_ATTACHMENTS ? canAttachTransactionToCase : undefined
          }
        />
      )}
      <ExportModal show={showExportModal} setShow={setShowExportModal} exportEntities={exportTransactions} />
    </SalvPage>
  );
};

interface CaseActionsProps {
  handle: string;
  selectedRows: InternalApiTransaction[];
}

const CaseActions = ({ handle, selectedRows }: CaseActionsProps) => {
  const navigate = useNavigate();
  const navigateToCase = () => navigate(getCasePath(handle), { state: { scrollTo: CASE_TRANSACTIONS_SCROLL_NAME } });
  const dispatch = useDispatch();
  const attachCaseTransactions = useAttachCaseTransactions();

  const handleAttachToCase = async () => {
    try {
      await attachCaseTransactions.mutateAsync({
        handle,
        // TODO if 'select all' was enabled then add all ids matching the search
        externalIds: selectedRows.map((transaction) => transaction.id),
      });
      dispatch(showFeedback({ message: 'Transactions attached to case successfully', success: true }));
      navigateToCase();
    } catch {
      dispatch(showFeedback({ message: 'Failed to attach transactions to case', success: false }));
    }
  };

  return (
    <div style={{ display: 'flex', gap: '8px' }}>
      <SalvButton variant='muted' label='Cancel' onClick={navigateToCase} size={Size.SMALL} />
      <SalvButton
        label={`Attach ${selectedRows.length > 0 ? `(${selectedRows.length})` : ''}`}
        onClick={handleAttachToCase}
        size={Size.SMALL}
        disabled={selectedRows.length === 0 || attachCaseTransactions.isPending}
      />
    </div>
  );
};

export default TransactionSearch;
